import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import EmojiPicker from 'emoji-picker-react';
import { find as linkifyFind } from 'linkifyjs';
import defaultUrlImage from '../../assets/chaticons/url-default.jpg';

import { useThreadApi } from '../../Services/ThreadApiProvider';
import { useClickOutside } from '../../Hooks/useClickOutside';
import FileUploader from './FileUploader';
import AudioMessageRecorder from './AudioMessageRecorder';

const defaultChatSettings = {
  emailInputOn: false,
  allowImageUpload: true,
  allowFileUpload: true,
  allowAudioMessages: false,
  botResponseDelay: 2000,
};

const ChatInputContainer = ({ content }) => {
  const { thread, loadingMessages, sendMessage } = useThreadApi();

  const [isActive, setIsActive] = useState(false);
  const [request, setRequest] = useState(content);

  const [preuploadedFiles, setPreuploadedFiles] = useState([]);
  const [clearUploader, setClearUploader] = useState(false);
  const [showRecorder, setShowRecorder] = useState(false);
  const [audioMessageSubmitted, setAudioMessageSubmitted] = useState(false);
  const [pendingAudioData, setPendingAudioData] = useState(null);
  const [clearAudio, setClearAudio] = useState(false);

  const inputContainerRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const emojiButtonRef = useRef(null);

  useEffect(() => {
    if (pendingAudioData) setIsButtonEnabled(true);
  }, [pendingAudioData]);

  useEffect(() => {
    setIsActive(thread?.is_active && !loadingMessages);
  }, [thread, loadingMessages]);

  useEffect(() => {
    setRequest(content);
  }, [content]);

  const [isButtonEnabled, setIsButtonEnabled] = useState(false);

  useEffect(() => {
    const hasText = request.trim() !== '';
    const hasFiles = preuploadedFiles.length > 0;
    const hasAudio = Boolean(pendingAudioData);

    setIsButtonEnabled(isActive && (hasText || hasFiles || hasAudio));
  }, [isActive, request, preuploadedFiles, pendingAudioData]);

  const [messageFlags, setMessageFlags] = useState({
    isSimpleText: true,
    isURL: false,
    isImageSingle: false,
    isImageGallery: false,
    isAudio: false,
    isFile: false,
    isVideo: false,
    isHTML: false,
  });

  const setOnlyFlag = (flagName) => {
    setMessageFlags({
      isSimpleText: false,
      isURL: false,
      isImageSingle: false,
      isImageGallery: false,
      isAudio: false,
      isFile: false,
      isVideo: false,
      isHTML: false,
      [flagName]: true,
    });
  };

  const [urlPreviewData, setUrlPreviewData] = useState(null);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);

  useClickOutside(
    [emojiPickerRef, emojiButtonRef],
    () => setIsEmojiPickerOpen(false),
    isEmojiPickerOpen,
  );

  // =========================== URL detection =============================

  const detectURLInText = (text) => {
    const foundLinks = linkifyFind(text);
    if (foundLinks.length > 0) return foundLinks[0].href;
    return null;
  };

  const fetchURLMetadata = async (url) => {
    try {
      const response = await fetch(url);
      // Might throw CORS error if site does not allow cross-origin fetch
      const html = await response.text();

      // DOMParser
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Attempt to grab an image
      let imageTag =
        doc.querySelector('meta[name="twitter:image"]') ||
        doc.querySelector('meta[property="og:image"]');
      let descriptionTag =
        doc.querySelector('meta[name="description"]') ||
        doc.querySelector('meta[property="og:description"]');
      let titleTag = doc.querySelector('title');

      const image = imageTag ? imageTag.getAttribute('content') : null;
      const description = descriptionTag ? descriptionTag.getAttribute('content') : null;
      const title = titleTag ? titleTag.innerText : null;

      console.log('Retrieved image tag: ', image);
      console.log('Retrieved description: ', description);
      console.log('Retrieved title: ', title);

      return { image, description, title };
    } catch (error) {
      console.warn('Fetching URL metadata failed:', error);
      return null;
    }
  };

  const handleTextChange = async (e) => {
    const newText = e.target.value;
    setRequest(newText);
    const foundUrl = detectURLInText(newText);
    if (foundUrl) {
      const metaData = await fetchURLMetadata(foundUrl);
      if (metaData) {
        setUrlPreviewData({
          url: foundUrl,
          image: metaData.image,
          description: metaData.description,
          title: metaData.title,
        });
        setOnlyFlag('isURL');
      }
    } else {
      setUrlPreviewData(null);
      setMessageFlags((old) => ({ ...old, isURL: false, isSimpleText: true }));
    }
  };

  // =========================== URL detection =============================

  const generateTimestamp = () => {
    return new Date().toISOString();
  };

  const determineMessageType = (flags) => {
    if (flags.isURL) return 'url';
    if (flags.isImageSingle) return 'image-single';
    if (flags.isImageGallery) return 'image-gallery';
    if (flags.isAudio) return 'audio_message';
    if (flags.isFile) return 'file';
    if (flags.isVideo) return 'video';
    if (flags.isHTML) return 'HTML';
    if (flags.isSimpleText) return 'simple_text';
    return 'simple_text';
  };

  const buildMessage = (requestText, chatId, flags, jsonData = {}) => {
    const timestamp = generateTimestamp();
    const finalText = requestText;
    let messageObj = {
      id: `msg-${Date.now()}`,
      sender: 'assistant',
      type: 'human',
      text: requestText,
      timestamp,
      chatId,
    };

    const type = determineMessageType(flags);
    switch (type) {
      case 'url':
        messageObj = {
          ...messageObj,
          typeOfMedia: 'url',
          url: jsonData.url || '',
          image: jsonData.image || defaultUrlImage,
          description: jsonData.description || '',
          title: jsonData.title || jsonData.url,
        };
        break;
      case 'image-single':
        messageObj = {
          ...messageObj,
          typeOfMedia: 'image',
          gallery: 'false',
          images: jsonData.images || [],
        };
        break;
      case 'image-gallery':
        messageObj = {
          ...messageObj,
          typeOfMedia: 'image',
          gallery: 'true',
          images: jsonData.images || [],
        };
        break;
      case 'audio_message':
        messageObj = {
          ...messageObj,
          typeOfMedia: 'audio_message',
          file: jsonData.file || '',
          audioBlobUrl: jsonData.audioBlobUrl,
          audioDataUrl: jsonData.audioDataUrl,
          playbackTime: jsonData.playbackTime || '',
          histogram: jsonData.histogram || [],
        };
        break;
      case 'file':
        messageObj = {
          ...messageObj,
          typeOfMedia: 'file',
          url: jsonData.url || '',
          fileName: jsonData.fileName || '',
          extension: jsonData.extension || '',
          size: jsonData.size || '',
          base64File: jsonData.base64File || '',
        };
        break;
      case 'video':
        messageObj = {
          ...messageObj,
          typeOfMedia: 'video',
          url: jsonData.url || '',
          fileName: jsonData.fileName || '',
          extension: jsonData.extension || '',
          size: jsonData.size || '',
          playbackTime: jsonData.playbackTime || '',
        };
        break;
      case 'HTML':
        messageObj = {
          ...messageObj,
          typeOfMedia: 'HTML',
          code: jsonData.code || '',
        };
        break;
      default:
        messageObj = {
          ...messageObj,
          typeOfMedia: 'simple_text',
        };
        break;
    }
    return messageObj;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isButtonEnabled) {
        handleSubmit();
      }
    }
  };

  const handleSubmit = async () => {
    if (!isActive) {
      console.warn('Chat is not active. Cannot send message.');
      return;
    }

    const text = request.trim();
    const hasText = text.length > 0;
    const hasFiles = preuploadedFiles.length > 0;
    const hasAudio = Boolean(pendingAudioData);

    if (!hasText && !hasFiles && !hasAudio) return;

    const buildImagesOrFilesJson = () => {
      if (preuploadedFiles.length === 0) return { images: [], files: [] };

      const allImages = preuploadedFiles.every((it) => it.base64Preview);
      if (allImages) {
        const images = preuploadedFiles.map((f) => ({
          size: `${f.sizeMB}`,
          base64: f.base64Preview,
          url: URL.createObjectURL(f.file),
        }));
        return { images, files: [] };
      } else {
        const files = preuploadedFiles.map((f) => ({
          fileName: f.name,
          base64File: f.base64File,
          extension: f.extension,
          size: `${f.sizeMB}`,
          url: URL.createObjectURL(f.file),
        }));
        return { images: [], files };
      }
    };

    if (hasFiles) {
      const { images, files } = buildImagesOrFilesJson();

      if (images.length > 0) {
        const flags =
          images.length === 1
            ? {
                isSimpleText: Boolean(text),
                isURL: false,
                isImageSingle: true,
                isImageGallery: false,
                isAudio: false,
                isFile: false,
                isVideo: false,
                isHTML: false,
              }
            : {
                isSimpleText: Boolean(text),
                isURL: false,
                isImageSingle: false,
                isImageGallery: true,
                isAudio: false,
                isFile: false,
                isVideo: false,
                isHTML: false,
              };

        const imgMsg = buildMessage(text, thread.id, flags, { images });
        sendMessage(imgMsg);
      } else if (files.length > 0) {
        const fileMsg = buildMessage(
          text,
          thread.id,
          {
            isSimpleText: Boolean(text),
            isURL: false,
            isImageSingle: false,
            isImageGallery: false,
            isAudio: false,
            isFile: true,
            isVideo: false,
            isHTML: false,
          },
          files.length === 1
            ? {
                url: files[0].url,
                fileName: files[0].fileName,
                extension: files[0].extension,
                size: files[0].size,
                base64File: files[0].base64File,
              }
            : { multipleFiles: files },
        );
        sendMessage(fileMsg);
      }

      setPreuploadedFiles([]);
      setClearUploader(true);
      setRequest('');
      return;
    }

    if (hasAudio) {
      const audioMsg = buildMessage(
        '',
        thread.id,
        { ...(pendingAudioData?.flags || {}), isAudio: true, isSimpleText: false },
        { ...(pendingAudioData?.jsonData || {}) },
      );
      sendMessage(audioMsg);

      setPendingAudioData(null);
      setClearAudio(true);
      setTimeout(() => setClearAudio(false), 0);
      setAudioMessageSubmitted(true);
      setRequest('');
      return;
    }

    if (hasText) {
      const foundUrl = detectURLInText(text);
      let newFlags;
      let jsonData = {};

      if (foundUrl && !urlPreviewData) {
        const metaData = await fetchURLMetadata(foundUrl);
        if (metaData) {
          newFlags = {
            isSimpleText: false,
            isURL: true,
            isImageSingle: false,
            isImageGallery: false,
            isAudio: false,
            isFile: false,
            isVideo: false,
            isHTML: false,
          };
          setUrlPreviewData({ ...metaData, url: foundUrl });
          await new Promise((resolve) => setTimeout(resolve, 500));
          jsonData = {
            url: foundUrl,
            image: metaData.image,
            description: metaData.description,
            title: metaData.title,
          };
        } else {
          newFlags = {
            isSimpleText: true,
            isURL: false,
            isImageSingle: false,
            isImageGallery: false,
            isAudio: false,
            isFile: false,
            isVideo: false,
            isHTML: false,
          };
        }
      } else if (urlPreviewData) {
        newFlags = {
          isSimpleText: false,
          isURL: true,
          isImageSingle: false,
          isImageGallery: false,
          isAudio: false,
          isFile: false,
          isVideo: false,
          isHTML: false,
        };
        jsonData = {
          url: urlPreviewData.url,
          image: urlPreviewData.image,
          description: urlPreviewData.description,
          title: urlPreviewData.title,
        };
      } else {
        newFlags = {
          isSimpleText: true,
          isURL: false,
          isImageSingle: false,
          isImageGallery: false,
          isAudio: false,
          isFile: false,
          isVideo: false,
          isHTML: false,
        };
      }

      const newMessage = buildMessage(text, thread.id, newFlags, jsonData);
      console.log('Sent text message - ', newMessage);
      sendMessage(newMessage);
      setRequest('');
      setUrlPreviewData(null);
    }
  };

  const toggleEmojiPicker = () => setIsEmojiPickerOpen(!isEmojiPickerOpen);
  const onEmojiClick = (emojiData) => {
    setRequest(request + emojiData.emoji);
    setIsEmojiPickerOpen(false);
  };

  return (
    <AnimatePresence>
      {isActive && (
        <div className="chat-input-container" ref={inputContainerRef}>
          <motion.div
            className="text-area-toolbar"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            <div className="input-area">
              <div className="request-input">
                <textarea
                  id="request"
                  rows="2"
                  value={request}
                  onChange={handleTextChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Message..."
                />
              </div>
            </div>

            <div className="chat-toolbar">
              <div className="chat-options">
                <button
                  ref={emojiButtonRef}
                  className="emozy-button"
                  onClick={toggleEmojiPicker}
                ></button>
                <FileUploader
                  isImageUpload={true}
                  multiple={true}
                  maxFileSizeMB={10}
                  maxImageSizeMB={2}
                  allFormatsAllowed={false}
                  allowedFormats={['jpg', 'jpeg', 'png', 'webm', 'heic']}
                  onFilesChange={(files) => {
                    setPreuploadedFiles(files);
                  }}
                  clearUploader={clearUploader}
                  setClearUploader={setClearUploader}
                />
                <FileUploader
                  multiple={false}
                  maxFileSizeMB={10}
                  maxImageSizeMB={2}
                  allFormatsAllowed={false}
                  allowedFormats={['pdf', 'mp4', 'webm', 'mov', 'zip', 'ogg', 'opus']}
                  onFilesChange={(files) => {
                    setPreuploadedFiles(files);
                  }}
                  clearUploader={clearUploader}
                  setClearUploader={setClearUploader}
                />
                <AudioMessageRecorder
                  onClose={() => setShowRecorder(false)}
                  setIsButtonEnabled={setIsButtonEnabled}
                  buildMessage={buildMessage}
                  audioMessageSubmitted={audioMessageSubmitted}
                  setAudioMessageSubmitted={setAudioMessageSubmitted}
                  onAudioRecorded={({ flags, jsonData }) => {
                    setPendingAudioData({ flags, jsonData });
                    setIsButtonEnabled(true);
                  }}
                  clearAudio={clearAudio}
                  setClearUploader={setClearUploader}
                />
              </div>
              <div className="submit-button-container">
                <button
                  className={`submit-button ${isButtonEnabled ? 'active' : ''}`}
                  onClick={handleSubmit}
                  disabled={!isButtonEnabled}
                ></button>
              </div>
            </div>

            {isEmojiPickerOpen && (
              <div className="emoji-picker-container" ref={emojiPickerRef}>
                <EmojiPicker
                  onEmojiClick={onEmojiClick}
                  autoFocusSearch={false}
                  emojiStyle="apple"
                  height={500}
                  width={348}
                  previewConfig={{ showPreview: false }}
                  skinTonesDisabled={true}
                />
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ChatInputContainer;
